import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import LockIcon from '@mui/icons-material/Lock';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { BottomSheet, ShowTooltipText, TextTimer } from '@ui';
import { fromUnixTime, subMonths } from 'date-fns';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getPreRecommendationDetails } from '../services/recommendation';
import { useTokens } from '../store/auth/useTokens';
import useBuyCycle from '../store/cycles/useBuyCycle';
import usePastBaskets from '../store/cycles/usePastBasket';
import useRecommendationDetails from '../store/cycles/useRecommendationDetails';
import useFreeTrialStatus from '../store/freeTrial/useFreeTrialStatus';
import { useBothUserPlans } from '../store/subscribe/usePlans';
import { useMyProfile, useUpdateProfile } from '../store/useAuthUser';
import { useMixedPanel } from '../store/useMixPanel';
import { formatRelativeOrAbsoluteDateTime } from '../utils/common';
import FreeTrialBanner from './FreeTrialBanner';
import { Caption } from './heading';

interface Recommendation {
  name?: string;
  code: string;
  price: string;
  change: string;
  type: 'positive' | 'negative';
}

const IntradayRecommendations = () => {
  const theme = useTheme();
  const router = useRouter();
  const params = useSearchParams();
  const { trackEvent } = useMixedPanel();
  const freeTrialRef = useRef<{ onFreeTrialBannerClicked: () => void } | null>(
    null
  );
  const { dataSource: pastBaskets } = usePastBaskets(
    subMonths(new Date(), 3).getTime()
  );

  const [
    isSubscribeToAccessBottomsheetOpen,
    setIsSubscribeToAccessBottomsheetOpen,
  ] = useState(false);
  const [, setBuyCycle] = useBuyCycle();
  const { long, short, refetch: refetchPlans } = useBothUserPlans();
  const {
    data: preRecommendationDetails,
    isLoading: isLoadingPreRecommendations,
  } = useQuery({
    queryKey: ['pre-recommendation-details--'],
    queryFn: () =>
      getPreRecommendationDetails(
        params?.get('live') === 'false' ? false : true
      ),
  });

  const {
    data: longRecommendationDetails,
    isLoading: isLoadingLongRecommendations,
  } = useRecommendationDetails({ enabled: true, basketType: 'hunter_long' });
  const {
    data: shortRecommendationDetails,
    isLoading: isLoadingShortRecommendations,
  } = useRecommendationDetails({ enabled: true, basketType: 'hunter_short' });
  const {
    data: freeTrialStatus,
    isLoading: isLoadingFreeTrialStatus,
    refetch: refetchFreeTrialStatus,
  } = useFreeTrialStatus();

  const { mutate: updateProfile, isPending: isUpdating } = useUpdateProfile();
  const { data: userProfile, isLoading: isLoadingProfile } = useMyProfile();

  const [tabValue, setTabValue] = useState('buy');
  const activeList =
    tabValue === 'buy'
      ? longRecommendationDetails?.recommendations
      : shortRecommendationDetails?.recommendations;

  const isNoSkipPayment = useMemo(() => {
    return !long?.skipPayment && !short?.skipPayment;
  }, [long, short]);

  // isNoSkipPayment== true &&
  const { setUserDetails, userInfo } = useTokens();
  useEffect(() => {
    if (!isNoSkipPayment && freeTrialStatus?.freeTrialStatus !== 'active') {
      setUserDetails({
        ...userInfo,
        clientType: 'subscription',
      });
    } else if (freeTrialStatus?.freeTrialStatus === 'active') {
      setUserDetails({
        ...userInfo,
        clientType: 'free_trial',
      });
    }
  }, [
    isNoSkipPayment,
    freeTrialStatus?.freeTrialStatus,
    userInfo?.username,
    userInfo?.broker,
    setUserDetails,
  ]);

  const isFreeTrial =
    freeTrialStatus && freeTrialStatus?.freeTrialStatus == 'not_active';
  const dateAndTime = fromUnixTime(
    preRecommendationDetails?.nextRecommendationTime ?? 0
  );
  const hasNoPastBaskets = useMemo(
    () => pastBaskets.length === 0,
    [pastBaskets]
  );
  const hasPastBaskets = useMemo(() => pastBaskets.length !== 0, [pastBaskets]);
  const isEligibleForFreeTrial = useMemo(
    () => isFreeTrial && isNoSkipPayment && hasNoPastBaskets,
    [isFreeTrial, isNoSkipPayment, hasNoPastBaskets]
  );
  const isEligibleForSubscription = useMemo(
    () =>
      (!isFreeTrial && isNoSkipPayment) ||
      (isFreeTrial && isNoSkipPayment && hasPastBaskets),
    [isFreeTrial, isNoSkipPayment, hasPastBaskets]
  );

  const isEligibleForSubscriptionNoRecommendation = useMemo(
    () => isNoSkipPayment && !isFreeTrial,
    [isNoSkipPayment, isFreeTrial]
  );
  const isRecommendationSuccess = (status: string | undefined) =>
    status === 'success';

  const buttonText = isEligibleForFreeTrial
    ? 'START FREE TRIAL'
    : isNoSkipPayment &&
      !isRecommendationSuccess(
        tabValue === 'buy'
          ? preRecommendationDetails?.longStatus
          : preRecommendationDetails?.shortStatus
      )
      ? 'SUBSCRIBE'
      : isEligibleForSubscription
        ? 'SUBSCRIBE TO UNLOCK'
        : tabValue === 'buy'
          ? 'PLACE BUY ORDERS'
          : 'PLACE SELL ORDERS';

  const handleClick = () => {
    const type = tabValue === 'buy' ? 'hunter_long' : 'hunter_short';

    if (buttonText === 'SUBSCRIBE') {
      setIsSubscribeToAccessBottomsheetOpen(true);
      return;
    }

    if (isEligibleForFreeTrial) {
      trackEvent(`CLICK: Start Free Trial ${tabValue} `);
      freeTrialRef.current?.onFreeTrialBannerClicked();
      return;
    }
    if (isEligibleForSubscription) {
      trackEvent('CLICK: Subscribe to unlock', { type });
      return router.push(`/subscribe?type=${type}`);
    }

    refetchFreeTrialStatus();
    refetchPlans();
    setBuyCycle(null, type);

    trackEvent(`CLICK: Place ${tabValue} orders`);
    router.push(`/configure?type=${type}`);
  };

  useEffect(() => {
    if (!userProfile) return;

    const showGetStarted = userProfile?.preferences?.apps?.intraday
      ?.showGetStarted as any;

    if (showGetStarted !== false && showGetStarted !== 'false') {
      router.push('/get-started');
    }
  }, [userProfile, router]);

  const shouldShowButton =
    isRecommendationSuccess(
      tabValue === 'buy'
        ? preRecommendationDetails?.longStatus
        : preRecommendationDetails?.shortStatus
    ) || isNoSkipPayment;
  const shouldShowLock =
    isNoSkipPayment &&
    ((tabValue === 'sell' &&
      isRecommendationSuccess(preRecommendationDetails?.shortStatus)) ||
      (tabValue === 'buy' &&
        isRecommendationSuccess(preRecommendationDetails?.longStatus)));

  if (isLoadingLongRecommendations || isLoadingShortRecommendations) {
    return (
      <Stack gap={2}>
        <Skeleton height={300} />
      </Stack>
    );
  }

  const renderContent = (status: string, type: 'BUY' | 'SELL') => {
    const dateAndTime = fromUnixTime(
      preRecommendationDetails?.nextRecommendationTime ?? 0
    );
    const isBuy = type === 'BUY';

    const buyShrunk = preRecommendationDetails?.longStatus === 'basket_shrunk';
    const sellShrunk =
      preRecommendationDetails?.shortStatus === 'basket_shrunk';

    if (buyShrunk && sellShrunk) {
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body1" fontWeight={500}>
            No Recommendations Available
          </Typography>
          <Typography variant="body2" mt={2}>
            {`Based on evaluation of current market conditions we don't have stock
            recommendations at the moment`}
          </Typography>
          <Typography variant="body2" mt={2}>
            The next recommendations will be available in{' '}
            <TextTimer endTime={dateAndTime} />.
          </Typography>
        </Box>
      );
    }

    if (isBuy && buyShrunk) {
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body1" fontWeight={500}>
            No Recommendations Available
          </Typography>
          <Typography variant="body2" mt={2}>
            Based on evaluation of current market conditions, there are no buy
            stock recommendations at the moment.
          </Typography>
          <Typography variant="body2" mt={1}>
            The next recommendations will be available in{' '}
            <TextTimer endTime={dateAndTime} />.
          </Typography>
          <Typography variant="body2" mt={1}>
            However, we do have sell stock predictions available.
          </Typography>
        </Box>
      );
    }

    if (!isBuy && sellShrunk) {
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body1" fontWeight={500}>
            No Recommendations Available
          </Typography>
          <Typography variant="body2" mt={2}>
            Based on evaluation of current market conditions, there are no sell
            stock recommendations at the moment.
          </Typography>
          <Typography variant="body2" mt={2}>
            The next recommendations will be available in{' '}
            <TextTimer endTime={dateAndTime} />.
          </Typography>
          <Typography variant="body2" mt={1}>
            However, we do have sell stock predictions available.
          </Typography>
        </Box>
      );
    }
    if (status === 'after_hours') {
      return (
        <Box sx={{ mt: 1 }}>
          <>
            <Typography variant="body1" fontWeight={500}>
              Recommendations Unavailable
            </Typography>
            <Caption>
              Next recommendations available in{' '}
              <TextTimer endTime={dateAndTime ?? 0} />
            </Caption>
            <Typography variant="body2" mt={1}>
              Recommendations are only available between 9:30 AM and 11:30 AM on
              trading days.
            </Typography>
            <Typography variant="body2" mt={1}>
              Stay tuned for fresh recommendations{' '}
              {formatRelativeOrAbsoluteDateTime(dateAndTime)}.
            </Typography>
          </>
        </Box>
      );
    }
    return activeList?.map((item, index) => (
      <Box key={index} sx={{ pb: 1.5, mb: 1 }}>
        <Stack direction="row" alignItems="center">
          <Typography variant="body2" fontWeight={500}>
            {item.security}
          </Typography>
          {item?.rationale && (
            <ShowTooltipText tooltipContent={item?.rationale}>
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: theme?.palette?.grey[500],
                  cursor: 'pointer',
                }}
              />
            </ShowTooltipText>
          )}
        </Stack>
        <Typography variant="caption" sx={{ color: theme?.palette?.grey[500] }}>
          {item?.name}
        </Typography>
      </Box>
    ));
  };

  const getBottomSheetContent = (status: string) => {
    if (status === 'after_hours') {
      return (
        <Box>
          <Typography>
            Subscription will be active after the payment, however,
            recommendations are only available between 9:30 AM and 11:30 AM on
            trading days.
          </Typography>
          <Typography>
            Stay tuned for fresh recommendations{' '}
            {formatRelativeOrAbsoluteDateTime(dateAndTime)}.
          </Typography>
          <Stack
            sx={{ alignItems: 'center', justifyContent: 'center' }}
          ></Stack>
        </Box>
      );
    }
    if (status === 'basket_shrunk') {
      return (
        <Box>
          <Typography variant="body2">
            Subscription will be active after the payment, however, due to
            market conditions, there are no recommendations currently.
          </Typography>
          <Typography variant="body2">
            Expect the next set of recommendations{' '}
            {formatRelativeOrAbsoluteDateTime(dateAndTime)}.
          </Typography>
          <Stack
            sx={{ alignItems: 'center', justifyContent: 'center' }}
          ></Stack>
        </Box>
      );
    }
  };

  return (
    <Box>
      <Typography variant="h6">Intraday Recommendations</Typography>
      <Box
        sx={{
          backgroundColor: theme?.palette?.grey[200],
          borderRadius: 1,
          overflow: 'hidden',
          p: 0.5,
          mb: 2,
          mt: 2,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => {
            setTabValue(newValue);
            trackEvent(`CLICK: ${newValue} tab`);
          }}
          variant="fullWidth"
          sx={{
            '& .MuiTabs-indicator': { display: 'none' },
            '&.MuiTabs-root': {
              minHeight: '30px',
            },
            '& .MuiTab-root': {
              flex: 1,
              color: theme?.palette?.custom?.textColor,
              borderRadius: '3px',
              textTransform: 'none',
              backgroundColor: theme?.palette?.grey[200],
              '&.Mui-selected': {
                color:
                  tabValue === 'buy'
                    ? theme?.palette?.custom?.performance?.positive
                    : theme?.palette?.custom?.performance?.negative,
                backgroundColor: theme?.palette?.common?.white,
              },
            },
          }}
        >
          <Tab
            label={
              <span>
                BUY <TrendingUpIcon />
              </span>
            }
            value="buy"
            sx={{ minHeight: '30px', padding: '0px' }}
          />
          <Tab
            label={
              <span>
                SELL <TrendingDownIcon />
              </span>
            }
            value="sell"
            sx={{ minHeight: '30px', padding: '0px' }}
          />
        </Tabs>
      </Box>

      <Box sx={{ minWidth: '100%', display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            p: 2,
            minHeight: '250px',
            width: '100%',
            position: 'relative',
            border: `1px solid ${theme?.palette?.grey[300]}`,
          }}
        >
          <Box sx={{ filter: shouldShowLock ? 'blur(10px)' : 'none' }}>
            {/* Render Content for BUY or SELL */}
            {tabValue === 'buy'
              ? renderContent(
                preRecommendationDetails?.longStatus as any,
                'BUY'
              )
              : renderContent(
                preRecommendationDetails?.shortStatus as any,
                'SELL'
              )}
          </Box>
          {shouldShowLock && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: alpha(theme?.palette?.common?.white, 0.1),
                borderRadius: 2,
              }}
            >
              <LockIcon sx={{ fontSize: 64, color: 'black' }} />
            </Box>
          )}
          {shouldShowButton && (
            <Button
              variant="contained"
              fullWidth
              sx={{
                mt: 2,
                bgcolor:
                  shouldShowLock || isNoSkipPayment
                    ? theme?.palette?.common?.black
                    : theme?.palette?.primary?.main,
                borderRadius: 2,
              }}
              onClick={handleClick}
            >
              {buttonText}
            </Button>
          )}
        </Paper>
      </Box>

      <BottomSheet
        isOpen={isSubscribeToAccessBottomsheetOpen}
        onClose={() => {
          setIsSubscribeToAccessBottomsheetOpen(false);
        }}
        theme={{
          bottomSheetSx: {
            padding: 2,
          },
          closeButtonSx: {
            color: 'primary.main',
          },
        }}
        header={
          <Stack mb={2}>
            <Typography variant="h6" color="primary.main">
              Subscribe to Get Access
            </Typography>
          </Stack>
        }
        content={
          <Stack>
            {getBottomSheetContent(
              tabValue === 'buy'
                ? (preRecommendationDetails?.longStatus as any)
                : (preRecommendationDetails?.shortStatus as any)
            )}
          </Stack>
        }
        footer={
          <>
            <Stack direction={'row'} width={'100%'} spacing={2} mt={2}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setIsSubscribeToAccessBottomsheetOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => {
                  router.push(
                    `/subscribe?type=${tabValue === 'buy' ? 'hunter_long' : 'hunter_short'
                    }`
                  );
                }}
              >
                Continue to pay
              </Button>
            </Stack>
          </>
        }
      />
      <Box sx={{ display: 'none' }}>
        <FreeTrialBanner ref={freeTrialRef} />
      </Box>
    </Box>
  );
};

export default IntradayRecommendations;
